import React from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';

import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './components/Layout/MainLayout'; // Import MainLayout
import DashboardV2 from './components/DashboardV2/DashboardV2';
import RecipesPageV2 from './components/RecetasV2/RecipesPageV2';
import RecipeDetailV2 from './components/RecetaIndividualV2/RecipeDetailV2';
import MenuSemanalV3 from './components/MenuSemanalV3/WeeklyPlannerV3';
import ListaCompra from './components/ListaCompra';
import FoodStockPage from './components/FoodStockPage';
import BatchPlanDetailPage from './components/BatchCooking/BatchPlanDetailPage';
import BatchCookingWizard from './components/BatchCooking/BatchCookingWizard';
import Register from './components/Register';
import ProfileV2 from './components/PerfilV2/ProfileV2';
import NuevaRecetaWrapper from './components/NuevaRecetaWrapper';
import ShoppingListsV2 from './components/ListasV2/ShoppingListsV2';
import ShoppingListDetailV2 from './components/ListasV2/ShoppingListDetailV2';
import GestionRecetas from './components/GestionRecetas';
import EditarRecetaWrapper from './components/EditarRecetaWrapper';
import GestionIngredientes from './components/GestionIngredientes';
import NuevoIngrediente from './components/NuevoIngrediente';
import EditarIngrediente from './components/EditarIngrediente';
import GestionarTaxonomias from './components/GestionarTaxonomias';
import EditarCategoriaReceta from './components/EditarCategoriaReceta';
import NuevaCategoriaReceta from './components/Crear/NuevaCategoriaReceta';
import NuevoTipoDeCocina from './components/Crear/NuevoTipoDeCocina';
import EditarTipoDeCocina from './components/EditarTipoDeCocina';
import EditarRestriccion from './components/EditarRestriccion';
import NuevaRestriccion from './components/Crear/NuevaRestriccion';
import GestionUsuarios from './components/GestionUsuarios';
import EditarUsuario from './components/EditarUsuario';
import Onboarding from './components/Onboarding/Onboarding';
import EditarAlergeno from './components/Editar/EditarAlergeno';
import NuevoAlergeno from './components/Crear/NuevoAlergeno';
import EditarCategoriaIngrediente from './components/Editar/EditarCategoriaIngrediente';
import NuevaCategoriaIngrediente from './components/Crear/NuevoCategoriaIngrediente';
import GestionWeights from './components/Gestion/GestionWeights';
import GestionBMI from './components/Gestion/GestionBMI';
import GestionBodyFat from './components/Gestion/GestionBodyFat';
import ConfirmEmail from './components/Auth/ConfirmEmail';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import AIUsageDashboard from './components/AIUsageDashboard';
import AdminRoute from './components/AdminRoute';

// Layout wrapper for authenticated routes
const PrivateLayout = ({ children }) => {
  return (
    <PrivateRoute>
      <MainLayout>
        {children}
      </MainLayout>
    </PrivateRoute>
  );
};

// Las listas compartidas son públicas. Si hay una sesión activa conservamos la
// navegación de la app; para visitantes mostramos únicamente la lista.
const SharedListLayout = ({ children }) => {
  const { isAuthenticated, isAuthResolved } = useAuth();
  if (!isAuthResolved) return null;
  return isAuthenticated ? <MainLayout>{children}</MainLayout> : <div className="shared-list-layout">{children}</div>;
};

const LegacyListRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/listas/${id}`} replace />;
};

const LegacyRecipeRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/recetas/${id}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          {/* Header removed, replaced by Sidebar in PrivateLayout */}
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Root: redirige según autenticación */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={
              <PrivateLayout>
                <DashboardV2 />
              </PrivateLayout>
            } />
            {import.meta.env.DEV && (
              <Route path="/preview/dashboard" element={
                <MainLayout>
                  <DashboardV2 preview />
                </MainLayout>
              } />
            )}
            {import.meta.env.DEV && (
              <Route path="/preview/menu-semanal" element={
                <MainLayout>
                  <MenuSemanalV3 preview />
                </MainLayout>
              } />
            )}
            {import.meta.env.DEV && (
              <Route path="/preview/recetas" element={
                <MainLayout>
                  <RecipesPageV2 preview />
                </MainLayout>
              } />
            )}
            {import.meta.env.DEV && <Route path="/preview/receta" element={<MainLayout><RecipeDetailV2 preview /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/fondo" element={<MainLayout><FoodStockPage /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/onboarding" element={<MainLayout><Onboarding /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/mediciones" element={<MainLayout><GestionWeights /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/batch-cooking" element={<MainLayout><BatchCookingWizard initialRange={{ start: new Date().toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) }} /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/listas" element={<MainLayout><ShoppingListsV2 preview /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/listas/detalle" element={<MainLayout><ShoppingListDetailV2 preview /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/listas/crear-lista" element={<MainLayout><ListaCompra preview /></MainLayout>} />}
            {import.meta.env.DEV && <Route path="/preview/perfil" element={<MainLayout><ProfileV2 preview /></MainLayout>} />}
            {/* Rutas públicas */}
            <Route path="/registro" element={<Register />} />
            <Route path="/confirmar/:token" element={<ConfirmEmail />} />
            <Route path="/cambiar-contrasena" element={<ForgotPassword />} />
            <Route path="/restablecer-contrasena/:token" element={<ResetPassword />} />
            <Route path="/listas/:id" element={<SharedListLayout><ShoppingListDetailV2 /></SharedListLayout>} />
            <Route path="/listas-nuevo/:id" element={<LegacyListRedirect />} />

            {/* Rutas privadas */}
            <Route path="/recetas" element={<PrivateLayout><RecipesPageV2 /></PrivateLayout>} />
            <Route path="/recetas/:id" element={<PrivateLayout><RecipeDetailV2 /></PrivateLayout>} />
            <Route path="/recetas-nuevo/:id" element={<LegacyRecipeRedirect />} />
            <Route path="/editar-receta/:id" element={<PrivateLayout><AdminRoute><EditarRecetaWrapper /></AdminRoute></PrivateLayout>} />
            <Route path="/menu-semanal" element={<PrivateLayout><MenuSemanalV3 /></PrivateLayout>} />
            <Route path="/listas/crear-lista" element={<PrivateLayout><ListaCompra /></PrivateLayout>} />
            <Route path="/fondo" element={<PrivateLayout><FoodStockPage /></PrivateLayout>} />
            <Route path="/batch-cooking/:planId" element={<PrivateLayout><BatchPlanDetailPage /></PrivateLayout>} />
            <Route path="/perfil" element={<PrivateLayout><ProfileV2 /></PrivateLayout>} />
            <Route path="/recetas/crear" element={<PrivateLayout><AdminRoute><NuevaRecetaWrapper /></AdminRoute></PrivateLayout>} />
            <Route path="/listas" element={<PrivateLayout><ShoppingListsV2 /></PrivateLayout>} />
            <Route path="/dashboard-nuevo" element={<Navigate to="/dashboard" replace />} />
            <Route path="/menu-semanal-nuevo" element={<Navigate to="/menu-semanal" replace />} />
            <Route path="/menu-semanal-classic" element={<Navigate to="/menu-semanal" replace />} />
            <Route path="/recetas-nuevo" element={<Navigate to="/recetas" replace />} />
            <Route path="/listas-nuevo" element={<Navigate to="/listas" replace />} />
            <Route path="/listas-nuevo/crear-lista" element={<Navigate to="/listas/crear-lista" replace />} />
            <Route path="/perfil-nuevo" element={<Navigate to="/perfil" replace />} />
            <Route path="/perfil/mis-datos" element={<Navigate to="/perfil" replace />} />
            <Route path="/gestion-recetas" element={<PrivateLayout><AdminRoute><GestionRecetas /></AdminRoute></PrivateLayout>} />
            <Route path="/gestion-ingredientes" element={<PrivateLayout><GestionIngredientes /></PrivateLayout>} />
            <Route path="/crear-ingrediente" element={<PrivateLayout><NuevoIngrediente /></PrivateLayout>} />
            <Route path="/editar-ingrediente/:id" element={<PrivateLayout><EditarIngrediente /></PrivateLayout>} />
            <Route path="/gestion-taxonomias" element={<PrivateLayout><GestionarTaxonomias /></PrivateLayout>} />
            <Route path="/editar-categoria-receta/:id" element={<PrivateLayout><EditarCategoriaReceta /></PrivateLayout>} />
            <Route path="/nueva-categoria-receta" element={<PrivateLayout><NuevaCategoriaReceta /></PrivateLayout>} />
            <Route path="/nuevo-tipo-cocina" element={<PrivateLayout><NuevoTipoDeCocina /></PrivateLayout>} />
            <Route path="/editar-tipo-de-cocina/:id" element={<PrivateLayout><EditarTipoDeCocina /></PrivateLayout>} />
            <Route path="/editar-restriccion/:id" element={<PrivateLayout><EditarRestriccion /></PrivateLayout>} />
            <Route path="/gestion-usuarios" element={<PrivateLayout><GestionUsuarios /></PrivateLayout>} />
            <Route path="/consumo-ia" element={<PrivateLayout><AIUsageDashboard /></PrivateLayout>} />
            <Route path="/editar-usuario/:id" element={<PrivateLayout><EditarUsuario /></PrivateLayout>} />
            <Route path="/onboarding" element={<PrivateLayout><Onboarding /></PrivateLayout>} />
            <Route path="/editar-alergeno/:id" element={<PrivateLayout><EditarAlergeno /></PrivateLayout>} />
            <Route path="/nuevo-alergeo" element={<PrivateLayout><NuevoAlergeno /></PrivateLayout>} />
            <Route path="/editar-categoria-ingrediente/:id" element={<PrivateLayout><EditarCategoriaIngrediente /></PrivateLayout>} />
            <Route path="/nueva-categoria-ingrediente" element={<PrivateLayout><NuevaCategoriaIngrediente /></PrivateLayout>} />
            <Route path="/gestion-weights/:id" element={<PrivateLayout><GestionWeights /></PrivateLayout>} />
            <Route path="/gestion-bmi/:id" element={<PrivateLayout><GestionBMI /></PrivateLayout>} />
            <Route path="/gestion-bodyfat/:id" element={<PrivateLayout><GestionBodyFat /></PrivateLayout>} />

          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

// Componente interno para decidir la redirección de la ruta raíz
const RootRedirect = () => {
  const { isAuthenticated, isAuthResolved } = useAuth();
  if (!isAuthResolved) return null;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};
